import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { Button, Grid, TextField, useMediaQuery } from "@mui/material";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import moment from "moment";

import SelectField from "Components/Common/SelectField";
import InputFieldText from "Components/Common/InputFieldText";
import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import Backdrop from "Components/Common/Backdrop";
import { dateFormats } from "common/constant";
import { getOptionValue, tariff } from "common/enums";

import { useLazyBillingCalculationReportQuery } from "apis/Billing";
import { useGetBillingCycleOptionsQuery } from "apis/BillingCycle";
import { useLazyDownloadExcelQuery } from "slices/api";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { convertCamelTextToCapitalizeText } from "utils/convertCamelTextToCapitalizeText";
import BillCalculationTable from "./BillCalculationTable";

const RECORDS_PER_PAGE = 10;

const initialQuery = { meterNumber: "", consumerNumber: "", tariff: "" };

const BillCalculationReport = ({ t }) => {
  const [billCycleId, setBillCycleId] = useState("");
  const [query, setQuery] = useState(initialQuery);
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);

  const resetPageRef = useRef();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const [
    getBillCalculationData,
    { data: { data: tableData } = {}, isLoading, isFetching },
  ] = useLazyBillingCalculationReportQuery();
  const {
    data: { data: billingCycleOptions } = {},
    isLoading: billingCycleOptionLoading,
  } = useGetBillingCycleOptionsQuery();
  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();

  const sm = useMediaQuery("(max-width: 599px)");

  const downloadBillCalculationReport = async () => {
    try {
      await downloadExcel({
        endpoint: `/report/billing-cycle/${billCycleId}/bill-calculation-excel`,
        sheetName: "Billing Calculation",
      });
    } catch (error) {}
  };

  const tableColumns = useMemo(() => {
    const columns = [{ title: "Sr. No" }];
    if (tableData?.[0]) {
      Object.keys(tableData?.[0]).forEach((key) => {
        columns.push({
          title: convertCamelTextToCapitalizeText(key),
          field: key,
        });
      });
    }
    return columns;
  }, [tableData]);

  const updatedTableData = useMemo(() => {
    if (tableData) {
      return tableData.map((row) =>
        Object.entries(row).reduce((res, [key, value]) => {
          if (
            typeof value === "string" &&
            moment(value, dateFormats, true).isValid()
          ) {
            return {
              ...res,
              [key]: convertUTCDateToLocalDate(value).format("DD-MM-YYYY"),
            };
          }
          return { ...res, [key]: value };
        }, {})
      );
    }
  }, [tableData]);

  const updatedBillingCycleOptions = useMemo(
    () =>
      billingCycleOptions?.length
        ? billingCycleOptions
            .filter(
              ({ subscriptionType }) =>
                subscriptionType ===
                getOptionValue("subscriptionTypes", "Electricity")
            )
            .map(({ billDateDuration, id, status }) => ({
              label: billDateDuration,
              value: id,
              status,
            }))
        : [],
    [billingCycleOptions]
  );

  const tariffOptions = useMemo(
    () => [{ value: "", label: "All" }, ...tariff],
    [tariff]
  );

  useEffect(() => {
    if (updatedBillingCycleOptions?.length) {
      const billingCycleId =
        updatedBillingCycleOptions.find(
          ({ status }) => status !== getOptionValue("billingStatus", "Closed")
        )?.value ??
        updatedBillingCycleOptions.at(-1)?.value ??
        "";
      setBillCycleId(billingCycleId);
      getBillCalculations(billingCycleId, initialQuery);
    }
  }, [updatedBillingCycleOptions]);

  const getBillCalculations = async (id, query) => {
    try {
      await getBillCalculationData(id, query);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Backdrop open={isLoading || isFetching || isExcelFileDownloading} />
      <BreadCrumb title={t("Bill Calculation")} pageTitle={t("Billing")} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          getBillCalculations(billCycleId, query);
          handleResetPage();
        }}
      >
        <Grid container columnSpacing={2} rowSpacing={1}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              onChange={(_, { value }) => {
                setBillCycleId(value);
              }}
              value={
                updatedBillingCycleOptions.find(
                  ({ value }) => value === billCycleId
                ) ?? ""
              }
              options={updatedBillingCycleOptions}
              renderInput={(params) => {
                delete params.inputProps.id;
                delete params.id;
                return <TextField {...params} label={t("Select bill cycle")} />;
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Consumer Number")}
              name="consumerNumber"
              type="number"
              onChange={(e) => {
                setQuery({ ...query, consumerNumber: e.target.value });
              }}
              value={query.consumerNumber}
              placeholder={t("Enter consumer number")}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Meter Number")}
              onChange={(e) => {
                setQuery({ ...query, meterNumber: e.target.value });
              }}
              value={query.meterNumber}
              placeholder={t("Enter meter number")}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Tariff")}
              onChange={(_, { value }) => {
                setQuery({ ...query, tariff: value });
              }}
              options={tariffOptions}
              value={
                tariffOptions.find(({ value }) => value === query.tariff) ?? ""
              }
            />
          </Grid>

          <Grid item lg={5}>
            <Button variant="outlined" className="mb-2 me-2" type="submit">
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                getBillCalculations(billCycleId, initialQuery);
                setQuery(initialQuery);
                handleResetPage();
              }}
              className="mb-2 me-2"
            >
              {t("Clear")}
            </Button>
            <Button
              variant="contained"
              className="mb-2 me-2"
              onClick={downloadBillCalculationReport}
            >
              {t("Download")}
            </Button>
          </Grid>
        </Grid>
      </form>
      <BillCalculationTable
        columns={tableColumns}
        pageSize={RECORDS_PER_PAGE}
        isLoader={billingCycleOptionLoading}
        billCalculationData={updatedTableData}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        maxHeight={sm ? "calc(100vh - 300px)" : "calc(100vh - 250px)"}
      />
    </>
  );
};

BillCalculationReport.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(BillCalculationReport);
